<script setup lang="ts">
  import { DatePicker } from 'v-calendar'
  import { computed, ref, watch, onMounted, onUnmounted, inject, type ComputedRef } from 'vue'
  import { MAX_DATE, MIN_DATE, MOBILE_WIDTH, PICKER_MASKS } from './BaseDatePicker.constants'
  import type { Locale, InputMode } from '@/shared/types/common.types'
  import type { RangeDate } from './BaseDatePicker.types'
  import DatePickerInput from './components/DatePickerInput/DatePickerInput.vue'
  import CrossIcon from '@/shared/components/icons/CrossIcon.vue'
  import { BaseButton } from '@qic/ui-components'
  import ErrorsList from '@/shared/components/ErrorsList/ErrorsList.vue'

  interface Props {
    modelValue: RangeDate
    minDate: Date
    maxDate: Date
    errors: { start: string[]; end: string[] }
    startDateLabel: string
    endDateLabel: string
    locale?: Locale
    inputMode?: InputMode
    isEditable?: boolean
    clearable?: boolean
    mobileTitle: string
    isRange?: boolean
    inModal?: boolean
  }

  interface Emits {
    (e: 'update:modelValue', rangeDate: RangeDate): void
    (e: 'dayFocus', rangeDate: RangeDate): void
    (e: 'close'): void
  }

  const props = withDefaults(defineProps<Props>(), {
    minDate: () => MIN_DATE,
    maxDate: () => MAX_DATE,
    locale: 'en',
    errors: () => ({ start: [], end: [] }),
    inputMode: 'numeric',
    isEditable: true,
    clearable: true,
    mobileTitle: '',
    isRange: false,
    inModal: false,
  })

  const emit = defineEmits<Emits>()

  const dpRef = ref()
  const date = computed<RangeDate>({
    get: () => props.modelValue,
    set(newValue) {
      emit('update:modelValue', newValue)
    },
  })

  const isRTL = inject('isRTL') as ComputedRef<boolean>

  const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_WIDTH})`)
  const isMobile = ref(mediaQuery.matches)
  const handleResize = (event: MediaQueryListEvent) => {
    isMobile.value = event.matches
  }

  const isMobileDatepickerShowed = computed(() => isMobile.value && datePickerShowed.value)

  const layout = computed(() => {
    if (isMobile.value) {
      let monthCount = 12
      if (props.minDate && props.maxDate) {
        monthCount = (props.maxDate.getFullYear() - props.minDate.getFullYear()) * 12
        monthCount -= props.minDate.getMonth()
        monthCount += props.maxDate.getMonth() + 1
        if (monthCount < 0) monthCount = 0
        if (monthCount > 12) monthCount = 12
      }
      return {
        columns: 1,
        rows: monthCount,
        isExpanded: true,
      }
    } else {
      return {
        columns: props.isRange ? 2 : 1,
        rows: 1,
        isExpanded: false,
      }
    }
  })

  const datePickerShowed = ref<boolean>(false)
  const focusedInput = ref<null | number>(null)

  const isFirstInputFocused = computed(() => datePickerShowed.value && focusedInput.value === 0)
  const isSecondInputFocused = computed(() => datePickerShowed.value && focusedInput.value === 1)

  const fucusedDates = ref<RangeDate>({ start: null, end: null })
  const onDayFocus = (day: RangeDate) => {
    fucusedDates.value = day
    emit('dayFocus', day)
  }

  const onInputClick = (showPopover: Function, inputNumber: number) => {
    showPopover()
    focusedInput.value = inputNumber
  }

  const onClose = () => {
    dpRef.value.hidePopover()
    datePickerShowed.value = false
    emit('close')
  }

  watch(
    (): RangeDate => fucusedDates.value,
    (next: RangeDate, prev: RangeDate) => {
      const prevStart = new Date(prev.start || '')
      const nextStart = new Date(next.start || '')
      const nextEnd = new Date(next.end || '')
      const prevEnd = new Date(prev.end || '')
      const startChanged =
        prevStart.getDate() !== nextStart.getDate() || prevStart.getMonth() !== nextStart.getMonth()
      const endChanged =
        prevEnd.getDate() !== nextEnd.getDate() || prevEnd.getMonth() !== nextEnd.getMonth()
      if (startChanged || endChanged) {
        if (nextStart < prevStart) focusedInput.value = 0
        if (nextEnd > prevEnd) focusedInput.value = 1
      }
    }
  )

  const initialPage = computed(() => ({
    year: props.minDate.getFullYear(),
    month: props.minDate.getMonth() + 1,
  }))

  onMounted(() => {
    mediaQuery.addEventListener('change', handleResize)
    if (props.inModal) {
      setTimeout(() => {
        dpRef.value.showPopover()
      }, 20)
    }
  })

  onUnmounted(() => {
    mediaQuery.removeEventListener('change', handleResize)
  })
</script>

<template>
  <Teleport to="body" :disabled="!(isMobileDatepickerShowed || inModal)">
    <div
      v-if="!isMobileDatepickerShowed && inModal"
      class="range-datepicker__modal-bg"
      @click="onClose"
    ></div>
    <div
      class="range-datepicker"
      :class="{
        mobile: isMobileDatepickerShowed,
        modal: !isMobileDatepickerShowed && inModal,
        'is-range': props.isRange,
        single: !props.isRange,
        rtl: isRTL,
      }"
      @click.stop
    >
      <!-- @vue-ignore -->
      <DatePicker
        ref="dpRef"
        v-model="date"
        :update-on-input="false"
        :initial-page="initialPage"
        :is-range="props.isRange"
        :rows="layout.rows"
        :columns="layout.columns"
        :expanded="layout.isExpanded"
        :popover="false"
        trim-weeks
        :first-day-of-week="1"
        :masks="PICKER_MASKS"
        :locale="locale"
        :min-date="minDate"
        :max-date="maxDate"
        nav-visibility=""
        :step="1"
        @drag="onDayFocus"
        @popover-will-show="datePickerShowed = true"
        @popover-did-hide="onClose"
      >
        <template #default="{ inputValue, inputEvents, showPopover }">
          <div>
            <div
              v-show="isMobileDatepickerShowed || inModal"
              class="range-datepicker__mobile-header"
            >
              <div class="range-datepicker__mobile-header__title">{{ mobileTitle }}</div>
              <CrossIcon class="range-datepicker__mobile-header-close" @click="onClose" />
            </div>
            <div class="range-datepicker__control-row">
              <div class="range-datepicker__input">
                <div
                  class="range-datepicker__input-hidden"
                  @click="onInputClick(showPopover, 0)"
                ></div>
                <DatePickerInput
                  :value="props.isRange ? inputValue.start : inputValue"
                  :label="startDateLabel"
                  :focused="isFirstInputFocused"
                  :big="!(isMobile || inModal)"
                  :errors="errors.start"
                  disabled
                  v-on="inputEvents.start"
                  @input="props.isRange ? inputEvents.start.input : undefined"
                />
                <ErrorsList :errors="errors.start" />
              </div>
              <div class="range-datepicker__input">
                <div
                  class="range-datepicker__input-hidden"
                  @click="onInputClick(showPopover, 1)"
                ></div>
                <DatePickerInput
                  v-if="props.isRange"
                  :value="inputValue.end"
                  :label="endDateLabel"
                  :focused="isSecondInputFocused"
                  :big="!(isMobile || inModal)"
                  :errors="errors.end"
                  disabled
                  v-on="inputEvents.end"
                  @input="inputEvents.end.input"
                />
                <ErrorsList :errors="errors.end" />
              </div>
              <div
                v-if="isMobileDatepickerShowed || inModal"
                class="range-datepicker__mobile-button"
              >
                <slot name="fixed-footer" />
                <BaseButton medium icon="none" @click="onClose">
                  {{ $t('qic_dropdown_save_button') }}
                </BaseButton>
              </div>
            </div>
          </div>
        </template>
        <template #header-title="title">
          {{ title.title }}
        </template>
        <template v-if="!isMobileDatepickerShowed" #footer>
          <slot name="footer" />
        </template>
      </DatePicker>
    </div>
  </Teleport>
</template>

<style lang="scss">
  @import 'v-calendar/style.css';
  @import './BaseDatePicker';
</style>
